import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ApplicationLayout, Notification, Sidebar } from "@rio-cloud/rio-uikit";

import Button from "@rio-cloud/rio-uikit/Button";
import { AutoSuggestSuggestion } from "@rio-cloud/rio-uikit/components/autosuggest/AutoSuggest";
import Autosuggest from "src/components/Autosuggest";
import { ISandboxMarker } from "src/components/Map/interfaces/ISandboxMarker";
import { createInterestPoint } from "src/services/interestPoints";
import { fetchAutosuggest } from "src/services/routes";
import { CreateInterestPointPayload } from "src/types/interestPoints";
import RioMap from "../../components/Map/index";
import { MapDrawingProvider } from "../../providers/MapDrawingContext";

export enum PoiCategory {
  RESTAURANT = "Restaurante",
  HOTEL = "Hotel",
  PARKING_FACILITY = "Estacionamento",
  FUEL_STATION = "Posto de combustível",
  ATM = "Caixa eletrônico",
  HOSPITAL = "Hospital",
  PHARMACY = "Farmácia",
  SUPERMARKET = "Supermercado",
  CAR_DEALER = "Concessionária",
  CAR_RENTAL = "Locadora de veículos",
  CHARGING_STATION = "Estação de recarga",
  REST_AREA = "Área de descanso",
  TRUCK_STOP = "Parada de caminhão",
  WEIGH_STATION = "Balança rodoviária",
  TOLL_BOOTH = "Pedágio",
  CUSTOMS = "Alfândega",
  REPAIR_SERVICE = "Oficina",
  POLICE = "Polícia",
  FIRE_DEPARTMENT = "Bombeiros",
  SCHOOL = "Escola",
  UNIVERSITY = "Universidade",
  BANK = "Banco",
  SHOPPING_CENTER = "Shopping",
  CINEMA = "Cinema",
  MUSEUM = "Museu",
  PARK = "Parque",
}

const InterestPointsCreateScreen = () => {
  const [selectedCategory, setSelectedCategory] = useState<PoiCategory>(PoiCategory.RESTAURANT);
  const [name, setName] = useState("");
  const [userName, setUserName] = useState('');
  const [address, setAddress] = useState("");
  const [marker, setMarker] = useState<ISandboxMarker | null>(null);
  const navigate = useNavigate();

  const createPoi = async () => {
    if (!marker) return;

    const categoriaKey =
      Object.keys(PoiCategory).find((key) => PoiCategory[key as keyof typeof PoiCategory] === selectedCategory) || "";
    const payload: CreateInterestPointPayload = {
      nome: name,
      // apelido: userName,
      categoria: categoriaKey?.toLowerCase()?.replace(/_/g, "-"),
      latitude: marker.position.lat,
      longitude: marker.position.lng,
      endereco: address,
    };

    try {
      await createInterestPoint(payload);
      Notification.success("Ponto de interesse criado com sucesso!");
      navigate("/routing/interest-points");
    } catch (error) {
      Notification.error("Erro ao criar ponto de interesse.");
    }
  };

  const fetchDataAutosuggestAddress = async (text: string) => {
    const response = await fetchAutosuggest(text);
    const data: AutoSuggestSuggestion[] = [];
    response.forEach((i) => data.push({ label: i.title, ...i } as any));

    return data;
  };

  const handleAddAddressMarker = (addressValue: string, position: { lat: number; lng: number }) => {
    setAddress(addressValue);
    const newMarker: ISandboxMarker = {
      id: `poi-${Date.now()}`,
      type: "customerPoiType",
      position,
      markerProps: {
        name: name,
        markerColor: "bg-map-marker-poi",
        iconNames: ["rioglyph rioglyph-map-marker"],
        anchorSize: "md",
      },
    };
    setMarker(newMarker);
  };

  const handleCancel = () => {
    navigate("/routing/interest-points");
  };

  const isFormValid = name.trim() !== "" && marker !== null;

  useEffect(() => {
      if (marker) {
        setMarker({
          ...marker,
          markerProps: {
            ...marker.markerProps,
            name: userName || name,
          }
        })
      }
    }, [name, userName])

  return (
    <MapDrawingProvider>
      <ApplicationLayout.Sidebar className="right z-index-2 height-100pct">
        <Sidebar
          title="Criar ponto de interesse"
          closed={false}
          onClose={() => { }}
          disableClose={true}
          position={Sidebar.RIGHT}
          width={500}
        >
          <div className="padding-left-20 padding-top-20 padding-right-20 padding-bottom-20 display-flex flex-column height-100pct">
            <div className="form-group">
              <label form="title">Nome</label>
              <input
                type="text"
                className="form-control"
                id="title"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            {/* <div className="form-group">
              <label form="title">Apelido</label>
              <input
                type="text"
                className="form-control"
                id="title"
                value={userName}
                onChange={(e) => setUserName(e.target.value)}
              />
            </div> */}

            <div className="form-group">
              <label htmlFor="interest-point-address">Endereço</label>
              <Autosuggest
                defaultValue={address}
                clearValue={false}
                placeholder="Digite o endereço"
                onSuggestionSelected={async (e: any, i: any) =>
                  handleAddAddressMarker(i.suggestionValue, i.suggestion.position)
                }
                fetchData={fetchDataAutosuggestAddress}
                icon="rioglyph rioglyph-search"
                onClear={() => {
                  setAddress("");
                }}
              />
            </div>
            <div className="form-group">
              <label form="checkpoint_type">Categoria</label>
              <select
                className="form-control"
                id="select"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value as PoiCategory)}
              >
                {Object.values(PoiCategory).map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <hr className="margin-top-20 margin-bottom-20 flex-grow-1" />

          <div
            className="display-flex justify-content-end gap-10 margin-right-20 margin-bottom-20"
            style={{ marginTop: "auto" }}
          >
            <Button onClick={handleCancel}>
              Cancelar
            </Button>
            <Button className="btn btn-primary" disabled={!isFormValid} onClick={createPoi}>
              Criar ponto de interesse
            </Button>
          </div>
        </Sidebar>
      </ApplicationLayout.Sidebar>
      <ApplicationLayout.Body innerClassName="padding-0" className="height-100pct">
        <RioMap
          apiKey={process.env.REACT_APP_RIO_MAP_KEY as string}
          position={marker ? marker.position : { lat: -23.5475, lng: -46.63611 }}
          useClusters={true}
          markers={marker ? [marker] : []}
          segments={[]}
          onMapClick={(address: string, position: { lat: number; lng: number }) => {
            handleAddAddressMarker(address, position);
          }}
        />
      </ApplicationLayout.Body>
    </MapDrawingProvider>
  );
};

export default InterestPointsCreateScreen;
